/**
 * tabla-generica-helpers.ts — Pure mutation helpers for TablaGenerica.
 *
 * All functions are pure: they take { columnas, filas } and return a new
 * { columnas, filas } without mutating the original.
 *
 * Key invariant (ADR-D1): column cells in rows are keyed by column id (NOT
 * index), so add/remove/reorder columns never silently re-maps cell data.
 *
 * Scenarios tested: S-9.1..S-9.7 (addColumn, removeColumn, addRow, removeRow,
 * renameColumn, setCell) + stable-ids invariant.
 */
import type { TablaGenerica } from "../../types.js";

/**
 * Adds a new column with the given id and title.
 * Adds an empty cell for the new column in every existing row.
 */
export function addColumn(tabla: TablaGenerica, id: string, titulo: string): TablaGenerica {
  return {
    columnas: [...tabla.columnas, { id, titulo }],
    filas: tabla.filas.map((fila) => ({ ...fila, [id]: "" })),
  };
}

/**
 * Removes the column with the given id.
 * Drops that column's cell from every row without affecting other columns.
 * No-op if the id does not exist.
 */
export function removeColumn(tabla: TablaGenerica, id: string): TablaGenerica {
  return {
    columnas: tabla.columnas.filter((c) => c.id !== id),
    filas: tabla.filas.map((fila) => {
      const next = { ...fila };
      delete next[id];
      return next;
    }),
  };
}

/**
 * Adds a new empty row. Each existing column gets an empty string cell.
 * Produces {} when the table has no columns.
 */
export function addRow(tabla: TablaGenerica): TablaGenerica {
  const emptyRow: Record<string, string> = {};
  for (const col of tabla.columnas) {
    emptyRow[col.id] = "";
  }
  return {
    columnas: tabla.columnas,
    filas: [...tabla.filas, emptyRow],
  };
}

/**
 * Removes the row at the given index (0-based).
 * No-op if the index is out of bounds.
 */
export function removeRow(tabla: TablaGenerica, rowIndex: number): TablaGenerica {
  if (rowIndex < 0 || rowIndex >= tabla.filas.length) return tabla;
  return {
    columnas: tabla.columnas,
    filas: tabla.filas.filter((_, i) => i !== rowIndex),
  };
}

/**
 * Renames the column with the given id to the new title.
 * Cell data is unaffected (cells use column id as key, not title).
 * No-op if the id does not exist.
 */
export function renameColumn(tabla: TablaGenerica, id: string, titulo: string): TablaGenerica {
  return {
    columnas: tabla.columnas.map((c) => (c.id === id ? { ...c, titulo } : c)),
    filas: tabla.filas,
  };
}

/**
 * Updates the cell value at (rowIndex, columnId).
 * No-op if the rowIndex is out of bounds.
 */
export function setCell(
  tabla: TablaGenerica,
  rowIndex: number,
  columnId: string,
  value: string,
): TablaGenerica {
  if (rowIndex < 0 || rowIndex >= tabla.filas.length) return tabla;
  return {
    columnas: tabla.columnas,
    filas: tabla.filas.map((fila, i) =>
      i === rowIndex ? { ...fila, [columnId]: value } : fila,
    ),
  };
}
